import { ROOM_MATCH_LOCK_STALE_MS } from "./roomGovernanceTypes";
import {
  isRoomMatchEndedOnLiveDoc,
  listEffectiveOngoingRoomSessionMatches,
  listOngoingRoomSessionMatches,
} from "./liveMatchBinding";

/**
 * @param {string | null | undefined} uid
 * @param {{ createdBy?: string, deputyRefereeUid?: string | null } | null} roomDoc
 * @returns {import('./roomGovernanceTypes').RoomGovernanceRole}
 */
export function resolveRoomGovernanceRole(uid, roomDoc) {
  if (!uid || !roomDoc) return "player";
  if (roomDoc.createdBy === uid) return "owner";
  if (roomDoc.deputyRefereeUid === uid) return "deputy";
  return "player";
}

export function isRoomRefereeRole(role) {
  return role === "owner" || role === "deputy";
}

/**
 * In-room session matches this uid currently manages (ready or live scoring).
 * @param {string | null | undefined} uid
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 */
export function countRefereeManagedOngoingMatches(uid, roomMatches, liveById = null) {
  if (!uid) return 0;
  const rows = liveById
    ? listEffectiveOngoingRoomSessionMatches(roomMatches, liveById)
    : listOngoingRoomSessionMatches(roomMatches);
  return rows.filter((m) => m?.managedBy === uid).length;
}

/**
 * True when the room already has an in-progress scored session (not pre-start "ready").
 * @param {{ matchStatus?: string; winner?: string | null; roomMatches?: import('./roomGovernanceTypes').RoomSessionMatch[] }} ctx
 */
export function hasActiveLiveRoomSession(ctx = {}) {
  const { matchStatus, winner, roomMatches } = ctx;
  if (matchStatus === "live" && !winner) return true;
  return (roomMatches || []).some((m) => m?.status === "live");
}

/**
 * Owner/deputy may **Go Live** only while they manage zero other ready/live session matches.
 * @param {string | null | undefined} uid
 * @param {{ createdBy?: string; deputyRefereeUid?: string | null } | null} matchDoc
 * @param {{ matchStatus?: string; winner?: string | null; roomMatches?: import('./roomGovernanceTypes').RoomSessionMatch[]; managingLocalCourt?: boolean }} ctx
 */
export function canInitiateNewRoomSessionMatch(uid, matchDoc, ctx = {}) {
  if (!uid || !matchDoc) return false;
  const role = resolveRoomGovernanceRole(uid, matchDoc);
  if (!isRoomRefereeRole(role)) return false;

  const { roomMatches, managingLocalCourt, liveById } = ctx;
  if (countRefereeManagedOngoingMatches(uid, roomMatches, liveById) > 0) return false;

  if (managingLocalCourt) {
    const anyOngoing = liveById
      ? listEffectiveOngoingRoomSessionMatches(roomMatches, liveById).length
      : listOngoingRoomSessionMatches(roomMatches).length;
    if (!anyOngoing) return false;
  }

  return true;
}

/**
 * Room creator may cancel ready/live session matches from the Matches hub.
 * @param {string | null | undefined} uid
 * @param {import('./roomGovernanceTypes').RoomSessionMatch | null | undefined} roomMatch
 * @param {{ createdBy?: string } | null | undefined} roomDoc
 */
export function canRoomOwnerCancelSessionMatch(uid, roomMatch, roomDoc) {
  if (!uid || !roomMatch || !roomDoc) return false;
  if (resolveRoomGovernanceRole(uid, roomDoc) !== "owner") return false;
  return roomMatch.status === "ready" || roomMatch.status === "live";
}

/**
 * @param {import('../registry/models/registryTypes').CourtTeam | null | undefined} teamA
 * @param {import('../registry/models/registryTypes').CourtTeam | null | undefined} teamB
 * @returns {string[]}
 */
export function collectParticipantEntryIdsFromCourtTeams(teamA, teamB) {
  const ids = [];
  const pushSlots = (team) => {
    (team?.slots || []).forEach((s) => {
      if (s?.registryEntryId) ids.push(s.registryEntryId);
    });
  };
  pushSlots(teamA);
  pushSlots(teamB);
  return [...new Set(ids)];
}

/**
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 * @param {{
 *   matchStatus?: string;
 *   winner?: string | null;
 *   teamA?: import('../registry/models/registryTypes').CourtTeam | null;
 *   teamB?: import('../registry/models/registryTypes').CourtTeam | null;
 * }} [opts] When the live court is active but `roomMatches` was never synced (e.g. refresh after rules deploy), derive ids from court teams for **live** scoring only.
 * @returns {Set<string>} registry entry ids in any live room match
 */
export function computeActiveParticipantEntryIds(roomMatches, opts = {}) {
  const set = new Set();
  const { matchStatus, teamA, teamB, winner, liveById } = opts;
  (roomMatches || []).forEach((m) => {
    if (m?.status !== "live" && m?.status !== "ready") return;
    if (isRoomMatchEndedOnLiveDoc(m, liveById)) return;
    (m.participantEntryIds || []).forEach((id) => {
      if (id) set.add(id);
    });
  });

  const ongoingGovernance = (
    liveById
      ? listEffectiveOngoingRoomSessionMatches(roomMatches, liveById)
      : listOngoingRoomSessionMatches(roomMatches)
  ).length > 0;
  const courtLiveScoring = matchStatus === "live" && !winner;
  const courtPreStart = matchStatus === "ready" && ongoingGovernance;

  if (courtLiveScoring || courtPreStart) {
    collectParticipantEntryIdsFromCourtTeams(teamA, teamB).forEach((id) => {
      set.add(id);
    });
  }

  return set;
}

/**
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 * @param {import('../registry/models/registryTypes').RegistryEntry[]} entries
 * @returns {Set<string>} auth uids currently in a live match
 */
export function computeActivePlayerUserIds(roomMatches, entries) {
  const entryIds = computeActiveParticipantEntryIds(roomMatches);
  const byId = new Map((entries || []).map((e) => [e.id, e]));
  const uids = new Set();
  entryIds.forEach((entryId) => {
    const row = byId.get(entryId);
    if (row?.userId) uids.add(row.userId);
  });
  return uids;
}

/**
 * @param {{
 *   entry: import('../registry/models/registryTypes').RegistryEntry;
 *   presence?: string;
 *   activeEntryIds: Set<string>;
 * }} ctx
 * @returns {import('./roomGovernanceTypes').RoomPlayerAvailability}
 */
export function resolvePlayerAvailability({ entry, presence, activeEntryIds }) {
  if (activeEntryIds.has(entry.id)) return "playing";
  if (presence === "offline" || presence === "disconnected") return "offline";
  return "available";
}

/**
 * Registry rows eligible for a new match (same rules as roster “available”).
 * @param {import('../registry/models/registryTypes').RegistryEntry[]} entries
 * @param {Record<string, string | undefined>} [presenceMap]
 * @param {import('./roomGovernanceTypes').RoomSessionMatch[]} roomMatches
 * @param {{ matchStatus?: string; winner?: string | null; teamA?: import('../registry/models/registryTypes').CourtTeam | null; teamB?: import('../registry/models/registryTypes').CourtTeam | null }} [liveCourtOpts]
 */
export function filterAvailableRegistryEntries(
  entries,
  presenceMap,
  roomMatches,
  liveCourtOpts = {}
) {
  const activeEntryIds = computeActiveParticipantEntryIds(roomMatches, liveCourtOpts);
  return (entries || []).filter(
    (entry) =>
      resolvePlayerAvailability({
        entry,
        presence: presenceMap?.[entry.id],
        activeEntryIds,
      }) === "available"
  );
}

/**
 * @param {{ lockedBy?: string | null, lockedAt?: { toMillis?: () => number } | number | null }} match
 * @param {number} [nowMs]
 */
export function isRoomMatchLockStale(match, nowMs = Date.now()) {
  if (!match?.lockedBy) return true;
  const raw = match.lockedAt;
  let lockedMs = 0;
  if (typeof raw === "number") lockedMs = raw;
  else if (raw && typeof raw.toMillis === "function") lockedMs = raw.toMillis();
  if (!lockedMs) return true;
  return nowMs - lockedMs >= ROOM_MATCH_LOCK_STALE_MS;
}

/**
 * @param {import('./roomGovernanceTypes').RoomSessionMatch | null} match
 * @param {string | null} uid
 * @param {number} [nowMs]
 */
export function canAcquireRoomMatchLock(match, uid, nowMs = Date.now()) {
  if (!match || !uid) return false;
  if (match.status === "completed" || match.status === "cancelled") return false;
  if (!match.lockedBy || match.lockedBy === uid) return true;
  return isRoomMatchLockStale(match, nowMs);
}
